import { Dialog } from '@base-ui/react';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormSelect } from '@components/formInputs/ControlledFormSelect';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { Icon } from '@components/icon/Icon';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { reportAppError } from '@shared/reportAppError';
import { buildPathWithSearchParams } from '@shared/utils/urlUtils';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { Drawer } from 'restaurantAdmin/components/drawer/Drawer';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { GUEST_BOOK_GUEST_PATH } from 'restaurantAdmin/paths';
import typography from '~styles/typography.scss';
import { createVipTier, type VipTier, updateVipTier } from './apiHelpers';
import styles from './CouponDrawer.scss';
import {
  VIP_TIER_DISCOUNT_OPTIONS,
  VIP_TIER_PERIOD_OPTIONS,
  VIP_TIER_RESERVATION_COUNT_THRESHOLD_OPTIONS,
  VIP_TIER_SPEND_AMOUNT_THRESHOLD_OPTIONS,
} from './vipTierOptions';

interface VipTierFormData {
  name: string;
  reservationCountThreshold: string;
  reservationCountTimeWindowDays: number;
  spendAmountDollarsThreshold: string;
  spendAmountTimeWindowDays: number;
  description: string;
  amountOffTablePercentage: number;
}

export interface VipTierDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  vipTier?: VipTier;
}

const periodFormValue = (days: number | null): number => days ?? 0;
const periodPayloadValue = (days: number): number | null =>
  days === 0 ? null : days;

export const VipTierDrawer = ({
  isOpen,
  onClose,
  onSaved,
  vipTier,
}: VipTierDrawerProps) => {
  const { id: restaurantId } = useRestaurant();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = !!vipTier;
  const drawerFieldLabelClassName = cx(
    typography.c2_20,
    styles.drawerFieldLabel,
  );

  const { control, handleSubmit, reset, setValue } = useForm<VipTierFormData>({
    defaultValues: {
      name: '',
      reservationCountThreshold: '',
      reservationCountTimeWindowDays: 0,
      spendAmountDollarsThreshold: '',
      spendAmountTimeWindowDays: 0,
      description: '',
      amountOffTablePercentage: 15,
    },
  });
  const spendThreshold = useWatch({
    control,
    name: 'spendAmountDollarsThreshold',
  });

  const reservationThreshold = useWatch({
    control,
    name: 'reservationCountThreshold',
  });

  const isSpendEnabled = !!spendThreshold;
  const isReservationEnabled = !!reservationThreshold;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    reset({
      name: vipTier?.name ?? '',
      reservationCountThreshold:
        vipTier?.reservationCountThreshold?.toString() ?? '',
      reservationCountTimeWindowDays: periodFormValue(
        vipTier?.reservationCountTimeWindowDays ?? null,
      ),
      spendAmountDollarsThreshold:
        vipTier?.spendAmountThreshold !== null &&
        vipTier?.spendAmountThreshold !== undefined
          ? (vipTier.spendAmountThreshold / 100).toString()
          : '',
      spendAmountTimeWindowDays: periodFormValue(
        vipTier?.spendAmountTimeWindowDays ?? null,
      ),
      description: vipTier?.description ?? '',
      amountOffTablePercentage: vipTier?.amountOffTablePercentage ?? 15,
    });
  }, [isOpen, reset, vipTier]);

  const handleViewTierGuests = () => {
    if (!vipTier) return;
    const params = new URLSearchParams();
    params.append('vipTierId', vipTier.id);
    navigate(
      buildPathWithSearchParams(
        generatePath(GUEST_BOOK_GUEST_PATH, { guestId: null }),
        params,
      ),
    );
  };

  const handleSave = handleSubmit(async (data) => {
    const reservationCountThreshold = data.reservationCountThreshold
      ? Number(data.reservationCountThreshold)
      : null;
    const spendAmountThreshold = data.spendAmountDollarsThreshold
      ? Math.round(Number(data.spendAmountDollarsThreshold) * 100)
      : null;

    if (reservationCountThreshold === null && spendAmountThreshold === null) {
      errorToast({ message: 'Add at least one VIP tier criterion.' });
      return;
    }

    setIsSaving(true);

    const payload = {
      name: data.name.trim(),
      reservationCountThreshold,
      reservationCountTimeWindowDays:
        reservationCountThreshold === null
          ? null
          : periodPayloadValue(data.reservationCountTimeWindowDays),
      spendAmountThreshold,
      spendAmountTimeWindowDays:
        spendAmountThreshold === null
          ? null
          : periodPayloadValue(data.spendAmountTimeWindowDays),
      description: data.description.trim() ? data.description.trim() : null,
      amountOffTablePercentage: Number(data.amountOffTablePercentage),
    };

    try {
      if (isEditing) {
        await updateVipTier(restaurantId, vipTier.id, payload);
        successToast({ message: 'VIP tier updated successfully' });
      } else {
        await createVipTier(restaurantId, payload);
        successToast({ message: 'VIP tier created successfully' });
      }
      onSaved();
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      }
      reportAppError(error);
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <Drawer
      drawerContentClassName={styles.vipTierDrawerContent}
      modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      side="right"
    >
      <div className={cx(styles.container, styles.vipTierContainer)}>
        <header className={styles.header}>
          <h1 className={cx(typography.h5)}>
            {isEditing ? `${vipTier.name} Settings` : 'Add VIP Tier'}
          </h1>
          <Dialog.Close
            render={({ onClick }) => (
              <button
                aria-label="Close VIP tier drawer"
                className={styles.closeButton}
                onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
                type="button"
              >
                <Icon name="close" />
              </button>
            )}
          />
        </header>
        <div className={cx(styles.content)}>
          <ControlledFormInput
            control={control}
            label="Name"
            labelClassName={drawerFieldLabelClassName}
            name="name"
            rules={{ required: 'Name is required' }}
            type="text"
          />
          <ControlledFormTextareaAutosize
            control={control}
            label="Description"
            labelClassName={drawerFieldLabelClassName}
            name="description"
          />
          <div className={styles.section}>
            <label className={cx(typography.h5, styles.fieldLabel)}>
              Criteria
            </label>
            <label className={cx(typography.b2, styles.perksDescription)}>
              A guest who meets any of these criteria will automatically be
              assigned to this tier
            </label>
            <div className={styles.criteriaBlock}>
              <label className={styles.checkboxRow}>
                <input
                  checked={isSpendEnabled}
                  type="checkbox"
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setValue('spendAmountDollarsThreshold', '');
                      setValue('spendAmountTimeWindowDays', 0);
                    } else {
                      setValue('spendAmountDollarsThreshold', '1000');
                    }
                  }}
                />
                <span>Money spent on tables</span>
              </label>
              {isSpendEnabled && (
                <div className={styles.criteriaFields}>
                  <ControlledFormSelect
                    control={control}
                    label="Amount"
                    labelClassName={drawerFieldLabelClassName}
                    name="spendAmountDollarsThreshold"
                    options={VIP_TIER_SPEND_AMOUNT_THRESHOLD_OPTIONS}
                  />
                  <ControlledFormSelect
                    control={control}
                    label="Period of Time"
                    labelClassName={drawerFieldLabelClassName}
                    name="spendAmountTimeWindowDays"
                    options={VIP_TIER_PERIOD_OPTIONS}
                  />
                </div>
              )}
            </div>
            <div className={styles.criteriaBlock}>
              <label className={styles.checkboxRow}>
                <input
                  checked={isReservationEnabled}
                  type="checkbox"
                  onChange={(e) => {
                    if (!e.target.checked) {
                      setValue('reservationCountThreshold', '');
                      setValue('reservationCountTimeWindowDays', 0);
                    } else {
                      setValue('reservationCountThreshold', '5');
                    }
                  }}
                />
                <span>Number of reservations made</span>
              </label>
              {isReservationEnabled && (
                <div className={styles.criteriaFields}>
                  <ControlledFormSelect
                    control={control}
                    label="Amount"
                    labelClassName={drawerFieldLabelClassName}
                    name="reservationCountThreshold"
                    options={VIP_TIER_RESERVATION_COUNT_THRESHOLD_OPTIONS}
                  />
                  <ControlledFormSelect
                    control={control}
                    label="Period of Time"
                    labelClassName={drawerFieldLabelClassName}
                    name="reservationCountTimeWindowDays"
                    options={VIP_TIER_PERIOD_OPTIONS}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.section}>
            <label className={cx(typography.h5, styles.fieldLabel)}>
              Perks
            </label>
            <label className={cx(typography.b2, styles.perksDescription)}>
              Configure benefits applied automatically to guests with this VIP
              status.
            </label>
            <div className={styles.perksCard}>
              <ControlledFormSelect
                control={control}
                label="Discount Percentage"
                labelClassName={drawerFieldLabelClassName}
                name="amountOffTablePercentage"
                options={VIP_TIER_DISCOUNT_OPTIONS}
                rules={{ required: 'Table discount is required' }}
              />
              <span className={styles.perkNote}>
                Discount isn’t available for table resales or offers.
              </span>
            </div>
          </div>
        </div>
        <footer className={cx(styles.footer, styles.vipTierFooter)}>
          {isEditing && (
            <Button
              className={styles.footerSecondaryAction}
              isDisabled={isSaving}
              label="View Tier Guests"
              onClick={handleViewTierGuests}
              variant={ButtonVariants.Tertiary}
            />
          )}
          <div className={styles.footerActions}>
            <Dialog.Close
              render={({ onClick }) => (
                <Button
                  isDisabled={isSaving}
                  label="Cancel"
                  onClick={
                    onClick as React.MouseEventHandler<HTMLButtonElement>
                  }
                  variant={ButtonVariants.Tertiary}
                />
              )}
            />
            <Button
              isDisabled={isSaving}
              isLoading={isSaving}
              label="Save"
              onClick={() => {
                void handleSave();
              }}
              variant={ButtonVariants.Primary}
            />
          </div>
        </footer>
      </div>
    </Drawer>
  );
};
